import { BrowserModule } from '@angular/platform-browser';
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  Provider,
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './routes/app-routing.module';
import { AppComponent } from './app.component';
import { IconsProviderModule } from './icons-provider.module';
import { FormsModule } from '@angular/forms';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { CommonModule, registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { GlobalErrorHandler } from '@core/services/interceptors/global-error-handler.interceptor';
import { TokenInterceptorService } from '@core/services/interceptors/token-interceptor';
import * as Sentry from '@sentry/angular';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import dashboardBlockedActions from '@dashboard/dashboard-store/dashboard-blocked-actions';
import { blockedActions } from '@course-planning-store';
import { EnvVarsService } from './env-vars.service';
import { ConfigSharedComponentsModule } from '@shared/components/config-shared-components.module';
import { DashboardConfigService } from '@root/app/services/dashboard-config/dashboard-config.service';

registerLocaleData(en);

const SentryProviders: Array<Provider | EnvironmentProviders> = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
];

const providers: Array<Provider | EnvironmentProviders> = [
  provideAppInitializer(() => {
    const service = inject(DashboardConfigService);
    return service.load();
  }),
  { provide: NZ_I18N, useValue: en_US },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TokenInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: GlobalErrorHandler,
    multi: true,
  },
];

const environment = new EnvVarsService();

if (
  environment.getEnvKey('production') &&
  environment.getEnvKey('IS_COMMERCIAL')
) {
  providers.push(...SentryProviders);
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    IconsProviderModule,
    FormsModule,
    CommonModule,
    BrowserAnimationsModule,
    ConfigSharedComponentsModule,
    StoreModule.forRoot(),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 70, // Retains last 25 states
      logOnly: true, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      connectInZone: true,
      actionsBlocklist: [...dashboardBlockedActions, ...blockedActions],
    }),
  ],
  providers: [...providers, provideHttpClient(withInterceptorsFromDi())],
})
export class AppModule {}
